import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getSupplyTerm } from '../../utils/supplyTerms';
import styles from './SubscriptionClauseModal.module.css';

// Exact dollars for legal commitment amounts — "$199" for whole amounts, "$198.50" otherwise
const fmtUsd = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return '$—';
  return `$${n.toFixed(2).replace(/\.00$/, '')}`;
};

const SubscriptionClauseModal = ({ isOpen, onClose, onSubmit, membershipTier, supplyMonths, userName = "", onSignatureCapture, initialSignatureData }) => {
  // True commitment term for the selected supply — the 60 Day tier (storage key 1)
  // is a real 2-month commitment; 3/6/12 map to their own month counts.
  const term = getSupplyTerm(supplyMonths);
  const termMonths = term.internalMonths;
  const monthlyRate = Number(membershipTier?.monthlyPrice);
  const hasRate = Number.isFinite(monthlyRate);
  // State for all 13 checkboxes
  const [checkboxes, setCheckboxes] = useState({
    subscription1: false,
    subscription2: false,
    subscription3: false,
    termination1: false,
    termination2: false,
    payment1: false,
    payment2: false,
    payment3: false,
    refunds1: false,
    refunds2: false,
    refunds3: false,
    alternatives1: false,
    alternatives2: false,
  });

  // State for YES/NO agreement (mutually exclusive radio)
  const [agreement, setAgreement] = useState(null);
  
  // Handler for YES radio - auto-checks all boxes
  const handleYesSelected = () => {
    setAgreement('yes');
    setCheckboxes({
      subscription1: true,
      subscription2: true,
      subscription3: true,
      termination1: true,
      termination2: true,
      payment1: true,
      payment2: true,
      payment3: true,
      refunds1: true,
      refunds2: true,
      refunds3: true,
      alternatives1: true,
      alternatives2: true,
    });
  };

  // Signature canvas state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Signature mode: "draw" or "adopt"
  const [signatureMode, setSignatureMode] = useState("draw");
  const [adoptedName, setAdoptedName] = useState(userName || "");
  const [signatureApplied, setSignatureApplied] = useState(false);

  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  const [shakeAgreement, setShakeAgreement] = useState(false);
  const [shakeSignature, setShakeSignature] = useState(false);
  const agreementRef = useRef(null);
  const signatureRef = useRef(null);

  // Update adoptedName when userName prop changes
  useEffect(() => {
    if (userName) {
      setAdoptedName(userName);
    }
  }, [userName]);

  // Load initial signature data from parent (sync with checkout page)
  useEffect(() => {
    if (isOpen && initialSignatureData) {
      if (initialSignatureData.mode === 'adopt') {
        setSignatureMode('adopt');
        if (initialSignatureData.adoptedName) {
          setAdoptedName(initialSignatureData.adoptedName);
          setSignatureApplied(true);
        }
      } else if (initialSignatureData.mode === 'draw' && initialSignatureData.dataUrl) {
        setSignatureMode('draw');
        // Draw the initial signature onto canvas
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            setHasSignature(true);
          };
          img.src = initialSignatureData.dataUrl;
        }
      }
    }
  }, [isOpen, initialSignatureData]);


  // Compute if submit is enabled based on all conditions
  const isSubmitEnabled = useMemo(() => {
    const allCheckboxesChecked = Object.values(checkboxes).every(Boolean);
    const agreedToTerms = agreement === 'yes';
    const signatureValid = signatureMode === "draw" ? hasSignature : (signatureApplied && adoptedName.trim() !== "");
    return allCheckboxesChecked && agreedToTerms && signatureValid;
  }, [checkboxes, agreement, hasSignature, signatureMode, signatureApplied, adoptedName]);

  const handleCheckboxChange = (id) => {
    setCheckboxes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  // Initialize canvas context when modal opens
  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#2d2d2d';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [isOpen]);

  // Get position relative to canvas
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  // Mouse event handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Switch signature mode
  const handleModeChange = (mode) => {
    setSignatureMode(mode);
    if (mode === "draw") {
      // Clear adopted signature when switching to draw
      setSignatureApplied(false);
    } else {
      // Clear drawn signature when switching to adopt
      clearSignature();
    }
  };

  // Apply adopted signature
  const handleApplySignature = () => {
    if (adoptedName.trim()) {
      setSignatureApplied(true);
    }
  };

  // Handle submit with validation
  const handleSubmit = () => {
    const errors = [];
    const allCheckboxesChecked = Object.values(checkboxes).every(Boolean);
    const signatureValid = signatureMode === "draw" ? hasSignature : (signatureApplied && adoptedName.trim() !== "");

    if (agreement === null) {
      errors.push('Final Agreement');
      setShakeAgreement(true);
      setTimeout(() => setShakeAgreement(false), 600);
    }
    if (!allCheckboxesChecked && agreement !== 'yes') {
      errors.push('All acknowledgment checkboxes');
    }
    if (!signatureValid) {
      errors.push('Signature');
      setShakeSignature(true);
      setTimeout(() => setShakeSignature(false), 600);
    }

    setValidationErrors(errors);

    if (errors.length > 0) {
      // Scroll to first error
      if (agreement === null && agreementRef.current) {
        agreementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!signatureValid && signatureRef.current) {
        signatureRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Capture signature data and pass to parent
    if (onSignatureCapture) {
      if (signatureMode === 'draw' && canvasRef.current) {
        onSignatureCapture({
          mode: 'draw',
          dataUrl: canvasRef.current.toDataURL('image/png'),
          adoptedName: null,
        });
      } else if (signatureMode === 'adopt') {
        onSignatureCapture({
          mode: 'adopt',
          dataUrl: null,
          adoptedName: adoptedName.trim(),
        });
      }
    }

    onSubmit({
      checkboxes,
      agreementResponse: agreement,
      signatureMode,
      signatureData: signatureMode === 'draw' && canvasRef.current ? canvasRef.current.toDataURL('image/png') : null,
      adoptedName: signatureMode === 'adopt' ? adoptedName.trim() : null,
    });
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Subscription Terms &amp; Conditions</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.documentContent}>
            <h3>SECTION X: SUBSCRIPTION COMMITMENTS AND EARLY TERMINATION</h3>

            <h4>Why This Commitment Exists</h4>
            <p>
              Your OvalCare membership gives you discounted member pricing on medications. A
              multi-month supply purchased at checkout front-loads those discounted months: you
              receive the full discounted supply up front, so your membership is owed for the full
              supply term. If you cancel your membership before the term ends, the remaining months
              of the term are charged in full.
            </p>

            <h4>1. Subscription Term Commitments</h4>
            <p>
              When you enroll in OvalCare services, you agree to a specific subscription term length
              (the "Commitment Period"). Available Commitment Periods include, but are not limited to:
            </p>
            <ul>
              <li>60 Day subscriptions (2 months)</li>
              <li>3-month subscriptions</li>
              <li>6-month subscriptions</li>
              <li>12-month subscriptions</li>
              <li>Month-to-month subscriptions (no commitment)</li>
            </ul>
            <p>
              Your selected Commitment Period represents your binding agreement to maintain active 
              membership and pay all fees for the entire duration of that period.
            </p>

            <h4>2. Payment Structure During Commitment Period</h4>
            <p>
              <strong>Monthly Billing:</strong> While your Commitment Period may extend for multiple months, 
              your payment method will be charged on a monthly basis for the monthly membership fee plus 
              any applicable prescription costs and shipping fees.
            </p>
            <p>
              <strong>Total Commitment Amount:</strong> Regardless of the monthly billing structure, you 
              acknowledge that you are financially obligated for the total amount due for your entire 
              Commitment Period. The total commitment amount is calculated as: [Monthly Membership Fee x 
              Number of Months in Commitment Period] plus any additional services or prescriptions ordered 
              during the term.
            </p>

            <h4>3. Early Termination and Accelerated Payment</h4>
            <p>
              <strong>Cancellation Before Commitment Period Ends:</strong> If you cancel your subscription, 
              terminate services, or fail to maintain an active payment method before the end of your 
              Commitment Period, you will be immediately charged the entire remaining balance owed for 
              the remainder of your Commitment Period (the "Early Termination Fee").
            </p>
            <p>
              <strong>Calculation of Early Termination Fee:</strong> The Early Termination Fee is calculated as follows:
            </p>
            <p className={styles.formula}>
              Early Termination Fee = (Monthly Membership Fee x Remaining Months in Commitment Period)
            </p>
            {membershipTier && hasRate && termMonths >= 2 && (
              <p className={styles.example}>
                <strong>Example (Your Plan):</strong> If you enrolled in a {term.label} subscription
                ({termMonths} months) at {fmtUsd(monthlyRate)}/month and cancel after 1 month, you will
                immediately be charged {fmtUsd(monthlyRate * (termMonths - 1))} ({termMonths - 1} remaining
                month{termMonths > 2 ? 's' : ''} x {fmtUsd(monthlyRate)}) as the Early Termination Fee.
              </p>
            )}
            {(!membershipTier || !hasRate || termMonths < 2) && (
              <p className={styles.example}>
                <strong>Example:</strong> If you enrolled in a 6-month subscription at $99/month and cancel
                after 3 months, you will immediately be charged $297 (3 remaining months x $99) as the
                Early Termination Fee.
              </p>
            )}

            <h4>4. Immediate Charge Upon Early Termination</h4>
            <p>
              <strong>Authorization for Immediate Charge:</strong> By accepting these Terms and enrolling 
              in a Commitment Period subscription, you explicitly authorize OvalCare to immediately charge 
              your payment method on file for the full Early Termination Fee upon:
            </p>
            <ul>
              <li>Voluntary cancellation of your subscription before the Commitment Period ends</li>
              <li>Failure to maintain a valid payment method on file</li>
              <li>Breach of these Terms and Conditions resulting in termination by OvalCare</li>
              <li>Disputed charges or chargebacks initiated during the Commitment Period</li>
            </ul>
            <p>
              <strong>Payment Method on File:</strong> You agree to maintain a valid payment method on file 
              throughout your entire Commitment Period. If your payment method expires, is declined, or 
              becomes invalid during your Commitment Period, you authorize OvalCare to charge any valid 
              payment method you have provided to collect the Early Termination Fee.
            </p>

            <h4>5. No Prorated Refunds for Early Termination</h4>
            <p>
              <strong>No Refunds:</strong> Membership fees paid prior to cancellation are non-refundable. 
              When you cancel during a Commitment Period, you will not receive a refund for any months 
              already paid, and you will be charged the Early Termination Fee for all remaining months.
            </p>
            <p>
              <strong>Services Cease Immediately:</strong> Upon cancellation during a Commitment Period, 
              your access to OvalCare services, including the OVAL App, telehealth consultations, and 
              prescription fulfillment services, will cease immediately after the Early Termination Fee 
              is processed, unless otherwise required by law.
            </p>

            <h4>6. Exceptions to Early Termination Fee</h4>
            <p>The Early Termination Fee may be waived only in the following limited circumstances:</p>
            <ul>
              <li><strong>Medical Contraindication:</strong> If your treating OvalCare provider determines that continuing services poses a medical risk and documents this determination in your medical record</li>
              <li><strong>Death of Member:</strong> Upon receipt of a valid death certificate</li>
              <li><strong>Relocation:</strong> If you relocate to a state where OvalCare services are not legally available, with proof of relocation</li>
              <li><strong>Military Deployment:</strong> Active duty military deployment orders requiring relocation for more than 90 days, with proof of orders</li>
            </ul>
            <p>
              All exception requests must be submitted in writing to OvalCare's customer service department 
              with supporting documentation. OvalCare reserves the right to verify all exception claims and 
              make final determinations regarding fee waivers.
            </p>

            <h4>7. Subscription Renewals After Commitment Period</h4>
            <p>
              <strong>Automatic Renewal:</strong> Upon completion of your initial Commitment Period, your 
              subscription will automatically renew on a month-to-month basis at the then-current monthly 
              membership rate unless you cancel at least 30 days before the end of your Commitment Period.
            </p>
            <p>
              <strong>No Further Commitment After Initial Term:</strong> After your initial Commitment Period 
              ends and your subscription converts to month-to-month, you may cancel at any time with 30 days 
              written notice without incurring an Early Termination Fee.
            </p>

            <h4>8. Changes to Commitment Period Terms</h4>
            <p>
              <strong>No Modifications During Term:</strong> Once you have selected and confirmed your 
              Commitment Period, you cannot change to a different commitment length until your current 
              Commitment Period expires. You cannot switch from a longer commitment (e.g., 12 months) to 
              a shorter commitment (e.g., 6 months) to reduce your Early Termination Fee obligation.
            </p>
            <p>
              <strong>Future Commitment Period Changes:</strong> OvalCare reserves the right to modify the 
              available Commitment Periods, pricing, and terms for future enrollments. Changes will not 
              affect your current Commitment Period terms.
            </p>

            <h4>9. Collection of Unpaid Balances</h4>
            <p>
              <strong>Collection Rights:</strong> If your payment method is declined when OvalCare attempts 
              to collect the Early Termination Fee, OvalCare reserves the right to:
            </p>
            <ul>
              <li>Attempt to charge any other payment methods you have provided</li>
              <li>Engage collection agencies to recover unpaid balances</li>
              <li>Report unpaid balances to credit reporting agencies</li>
              <li>Pursue legal action to recover amounts owed plus collection costs, interest, and attorney fees</li>
            </ul>
            <p>
              <strong>Your Responsibility:</strong> You remain liable for all amounts owed under your 
              Commitment Period regardless of whether OvalCare successfully charges your payment method 
              at the time of cancellation.
            </p>

            <h4>10. Acknowledgment of Financial Obligation</h4>
            <p>By enrolling in a Commitment Period subscription, you acknowledge that:</p>
            <ul>
              <li>You understand you are entering into a binding financial commitment for the entire Commitment Period</li>
              <li>You authorize OvalCare to charge your payment method for monthly fees and the full Early Termination Fee if you cancel early</li>
              <li>You have read and understand the Early Termination Fee calculation</li>
              <li>You understand that canceling early will result in immediate payment of all remaining months</li>
              <li>You understand that month-to-month subscriptions are available if you do not wish to make a term commitment</li>
            </ul>

            <div className={styles.importantNotice}>
              <strong>IMPORTANT: PLEASE READ THIS CAREFULLY BEFORE ENROLLING</strong>
              <p>I understand and acknowledge the following terms of my OvalCare subscription commitment:</p>
            </div>

            {/* MY SUBSCRIPTION SELECTION - 3 checkboxes */}
            <div className={styles.checkboxSection}>
              <h4>MY SUBSCRIPTION SELECTION</h4>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="subscription1"
                  checked={checkboxes.subscription1}
                  onChange={() => handleCheckboxChange('subscription1')}
                />
                <label htmlFor="subscription1">
                  I understand that I am selecting a commitment period subscription that requires payment for the full term
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="subscription2"
                  checked={checkboxes.subscription2}
                  onChange={() => handleCheckboxChange('subscription2')}
                />
                <label htmlFor="subscription2">
                  I understand that my subscription will be billed monthly but I am obligated for the entire commitment period
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="subscription3"
                  checked={checkboxes.subscription3}
                  onChange={() => handleCheckboxChange('subscription3')}
                />
                <label htmlFor="subscription3">
                  I understand that month-to-month subscriptions are available if I do not wish to commit to a term
                </label>
              </div>
            </div>

            {/* EARLY TERMINATION FEE - 2 checkboxes */}
            <div className={styles.checkboxSection}>
              <h4>EARLY TERMINATION FEE</h4>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="termination1"
                  checked={checkboxes.termination1}
                  onChange={() => handleCheckboxChange('termination1')}
                />
                <label htmlFor="termination1">
                  I understand that if I cancel before my commitment period ends, I will be charged an Early Termination Fee equal to all remaining monthly payments
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="termination2"
                  checked={checkboxes.termination2}
                  onChange={() => handleCheckboxChange('termination2')}
                />
                <label htmlFor="termination2">
                  I have read and understand the Early Termination Fee calculation formula and example provided above
                </label>
              </div>
            </div>

            {/* PAYMENT AUTHORIZATION - 3 checkboxes */}
            <div className={styles.checkboxSection}>
              <h4>PAYMENT AUTHORIZATION</h4>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="payment1"
                  checked={checkboxes.payment1}
                  onChange={() => handleCheckboxChange('payment1')}
                />
                <label htmlFor="payment1">
                  I authorize OvalCare to charge my payment method on file for monthly membership fees
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="payment2"
                  checked={checkboxes.payment2}
                  onChange={() => handleCheckboxChange('payment2')}
                />
                <label htmlFor="payment2">
                  I authorize OvalCare to immediately charge my payment method for the full Early Termination Fee if I cancel early
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="payment3"
                  checked={checkboxes.payment3}
                  onChange={() => handleCheckboxChange('payment3')}
                />
                <label htmlFor="payment3">
                  I agree to maintain a valid payment method on file throughout my entire commitment period
                </label>
              </div>
            </div>

            {/* NO REFUNDS - 3 checkboxes */}
            <div className={styles.checkboxSection}>
              <h4>NO REFUNDS</h4>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="refunds1"
                  checked={checkboxes.refunds1}
                  onChange={() => handleCheckboxChange('refunds1')}
                />
                <label htmlFor="refunds1">
                  I understand that membership fees already paid are non-refundable
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="refunds2"
                  checked={checkboxes.refunds2}
                  onChange={() => handleCheckboxChange('refunds2')}
                />
                <label htmlFor="refunds2">
                  I understand that canceling will not result in a refund for any months already paid
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="refunds3"
                  checked={checkboxes.refunds3}
                  onChange={() => handleCheckboxChange('refunds3')}
                />
                <label htmlFor="refunds3">
                  I understand that my access to services will cease immediately upon cancellation
                </label>
              </div>
            </div>

            {/* ALTERNATIVES AVAILABLE - 2 checkboxes */}
            <div className={styles.checkboxSection}>
              <h4>ALTERNATIVES AVAILABLE</h4>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="alternatives1"
                  checked={checkboxes.alternatives1}
                  onChange={() => handleCheckboxChange('alternatives1')}
                />
                <label htmlFor="alternatives1">
                  I understand that I could have chosen a month-to-month subscription with no commitment
                </label>
              </div>
              <div className={styles.checkbox}>
                <input
                  type="checkbox"
                  id="alternatives2"
                  checked={checkboxes.alternatives2}
                  onChange={() => handleCheckboxChange('alternatives2')}
                />
                <label htmlFor="alternatives2">
                  I am voluntarily choosing a commitment period subscription instead of a month-to-month option
                </label>
              </div>
            </div>

            {/* COMMITMENT SUMMARY - Dynamic values */}
            {membershipTier && hasRate && (
              <div className={styles.commitmentSummary}>
                <h4>COMMITMENT SUMMARY</h4>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Your Plan:</span>
                  <span className={styles.summaryValue}>{membershipTier.name} at {fmtUsd(monthlyRate)}/month</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Subscription Length:</span>
                  <span className={styles.summaryValue}>{term.label} ({termMonths} month{termMonths > 1 ? 's' : ''})</span>
                </div>
                <div className={styles.summaryHighlight}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Total Membership Commitment:</span>
                    <span className={styles.summaryValue}>{fmtUsd(monthlyRate)} x {termMonths} = {fmtUsd(monthlyRate * termMonths)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Early Termination Fee (if cancelled after 1 month):</span>
                    <span className={styles.summaryValue}>{fmtUsd(monthlyRate)} x {termMonths - 1} = {fmtUsd(monthlyRate * (termMonths - 1))}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Divider before Final Agreement */}
            <hr className={styles.agreementDivider} />

            {/* YES/NO Agreement - Radio buttons */}
            <div ref={agreementRef} className={`${styles.agreementSection} ${shakeAgreement ? styles.shakeError : ''}`}>
              <h4><span className={styles.agreementIcon}>⚠️</span> IMPORTANT: Final Agreement Required</h4>
              <p className={styles.agreementSubheading}>You must select one of the options below to continue</p>
              <div className={styles.radioGroup}>
                <div className={styles.radioYes}>
                  <input
                    type="radio"
                    id="agreeYes"
                    name="agreement"
                    value="yes"
                    checked={agreement === 'yes'}
                    onChange={handleYesSelected}
                  />
                  <label htmlFor="agreeYes">Yes, I agree to all the subscription, commitment, and early termination fee terms</label>
                </div>
                <div className={styles.radioNo}>
                  <input
                    type="radio"
                    id="agreeNo"
                    name="agreement"
                    value="no"
                    checked={agreement === 'no'}
                    onChange={() => setAgreement('no')}
                  />
                  <label htmlFor="agreeNo">No, I do not agree to the subscription, commitment, and early termination fee terms</label>
                </div>
              </div>
              {shakeAgreement && <p className={styles.validationMsg}>⚠ Please select Yes or No to continue</p>}
            </div>

            {/* SIGNATURE - Draw or Adopt */}
            <div ref={signatureRef} className={`${styles.signatureSection} ${shakeSignature ? styles.shakeError : ''}`}>
              <div className={styles.signatureHeader}>
                <h4>SIGNATURE</h4>
              </div>
              
              {/* Mode Toggle Tabs */}
              <div className={styles.signatureTabs}>
                <button
                  type="button"
                  className={`${styles.signatureTab} ${signatureMode === "draw" ? styles.activeTab : ""}`}
                  onClick={() => handleModeChange("draw")}
                >
                  Draw
                </button>
                <button
                  type="button"
                  className={`${styles.signatureTab} ${signatureMode === "adopt" ? styles.activeTab : ""}`}
                  onClick={() => handleModeChange("adopt")}
                >
                  Adopt
                </button>
              </div>

              {/* Draw Mode */}
              {signatureMode === "draw" && (
                <div className={styles.drawMode}>
                  <p className={styles.signatureInstruction}>
                    Draw your signature below:
                  </p>
                  <canvas
                    ref={canvasRef}
                    className={styles.signatureCanvas}
                    width={600}
                    height={200}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <div className={styles.drawControls}>
                    <button
                      type="button"
                      className={styles.clearBtn}
                      onClick={clearSignature}
                    >
                      Clear
                    </button>
                  </div>
                  {!hasSignature && (
                    <p className={styles.signatureHint}>Draw your signature above</p>
                  )}
                </div>
              )}

              {/* Adopt Mode */}
              {shakeSignature && <p className={styles.validationMsg}>⚠ Signature required</p>}

              {signatureMode === "adopt" && (
                <div className={styles.adoptMode}>
                  <p className={styles.signatureInstruction}>
                    Type your name to adopt as your signature:
                  </p>
                  <input
                    type="text"
                    className={styles.adoptInput}
                    value={adoptedName}
                    onChange={(e) => {
                      setAdoptedName(e.target.value);
                      setSignatureApplied(false);
                    }}
                    placeholder="Enter your full name"
                  />
                  {adoptedName.trim() && (
                    <div className={styles.signaturePreview}>
                      <span className={styles.previewLabel}>Preview:</span>
                      <div className={styles.cursiveSignature}>
                        {adoptedName}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className={`${styles.adoptBtn} ${signatureApplied ? styles.adopted : ""}`}
                    onClick={handleApplySignature}
                    disabled={!adoptedName.trim() || signatureApplied}
                  >
                    {signatureApplied ? "Signature Adopted ✓" : "Adopt Signature"}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

        <div className={styles.footer}>
          {validationErrors.length > 0 && (
            <div className={styles.validationSummary}>
              <p>Complete the following to submit:</p>
              <ul>
                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
          <button 
            type="button" 
            className={styles.submitBtn}
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionClauseModal;
